<template>
  <section class="car-gallery-section" id="car-gallery">
    <!-- Header and controls -->
    <div class="header-wrapper">
      <div class="section-header">
        <h1 class="section-title" data-aos="fade-up" data-aos-delay="300">
          Recommended for you
        </h1>
        <div
          class="title-underline"
          data-aos="slide-right"
          data-aos-delay="400"
        ></div>
        <div class="controls-wrapper">
          <button
            class="slider-arrow prev"
            @click="scrollLeft"
            aria-label="Previous cars"
            data-aos="fade-up"
            data-aos-delay="500"
          >
            <span>←</span>
          </button>
          <button
            class="slider-arrow next"
            @click="scrollRight"
            aria-label="Next cars"
            data-aos="fade-up"
            data-aos-delay="600"
          >
            <span>→</span>
          </button>
          <router-link
            to="/buy"
            class="view-all"
            data-aos="fade-up"
            data-aos-delay="700"
          >
            View all
          </router-link>
        </div>
      </div>
    </div>

    <!-- Slider -->
    <div
      class="slider-container"
      ref="sliderContainer"
      @mousedown="startDragging"
      @touchstart="startDragging"
      @mousemove="drag"
      @touchmove="drag"
      @mouseup="stopDragging"
      @touchend="stopDragging"
      @mouseleave="stopDragging"
    >
      <div
        class="slider-track"
        ref="sliderTrack"
        :style="{ transform: `translateX(${translateX}px)` }"
      >
        <div
          v-for="(car, index) in infiniteCars"
          :key="`${car.id}-${index}`"
          class="car-slide"
          @click="goToCarPage(car.id)"
        >
          <div class="car-card">
            <div class="car-image-wrapper">
              <img :src="car.image" :alt="car.name" class="car-image" />
            </div>
            <h3 class="car-name">{{ car.name }} {{ car.year }}</h3>
            <p class="car-price">
              ${{
                (car.has_sale ? car.sale_price : car.price).toLocaleString()
              }}
              <span v-if="car.has_sale" class="original-price">
                ${{ car.price.toLocaleString() }}
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import AOS from "aos";
import "aos/dist/aos.css";
import axios from "axios";
import API_CONFIG from "@/config/api.config.js"; // Import the config

export default {
  name: "CarGallerySlider",
  data() {
    return {
      isLoading: false,
      cars: [],
      baseUrl: API_CONFIG.baseUrl,
      translateX: 0, // Track offset in pixels
      slideWidth: 310, // Slide width (290px + 20px margin)
      isDragging: false,
      startX: 0,
      initialTranslateX: 0,
      autoplayInterval: null, // For automatic sliding
    };
  },
  computed: {
    // Create infinite carousel by triplicating the car array
    infiniteCars() {
      return [...this.cars, ...this.cars, ...this.cars];
    },
    totalWidth() {
      return this.infiniteCars.length * this.slideWidth;
    },
  },
  mounted() {
    this.fetchCars();
    AOS.init({
      once: true,
      offset: 50,
    });
    // Start autoplay after component is mounted
    this.startAutoplay();
  },
  beforeUnmount() {
    // Clear interval when component is destroyed
    this.stopAutoplay();
  },
  methods: {
    async fetchCars() {
      try {
        this.isLoading = true;
        const response = await axios.get(`${this.baseUrl}/api/cars/top10`);
        this.cars = response.data.map((car) => ({
          id: car.id,
          image: car.image
            ? `${this.baseUrl}${car.image}`
            : "https://via.placeholder.com/290x200?text=Car+Image",
          name: `${car.manufacturer} ${car.model}`,
          year: car.year,
          price: car.price,
          sale_price: car.sale_price,
          has_sale: car.has_sale,
        }));
      } catch (error) {
        console.error("Error fetching cars:", error);
      } finally {
        this.isLoading = false;
      }
    },
    goToCarPage(carId) {
      this.$router.push({ path: `/cars/${carId}`, hash: "#top" }).then(() => {
        window.scrollTo(0, 0);
      });
    },
    scrollLeft() {
      this.translateX += this.slideWidth; // Move one card to the left
      this.checkBoundaries();
    },
    scrollRight() {
      this.translateX -= this.slideWidth; // Move one card to the right
      this.checkBoundaries();
    },
    checkBoundaries() {
      const maxTranslate = 0; // Left edge
      const minTranslate = -this.totalWidth / 3; // Middle of duplicated content

      if (this.translateX > maxTranslate) {
        this.translateX = minTranslate + (this.translateX - maxTranslate);
      } else if (this.translateX < minTranslate) {
        this.translateX = maxTranslate - (minTranslate - this.translateX);
      }
    },
    startDragging(event) {
      this.isDragging = true;
      this.startX =
        event.type === "touchstart" ? event.touches[0].clientX : event.clientX;
      this.initialTranslateX = this.translateX;
      this.$refs.sliderTrack.style.transition = "none"; // Remove animation during dragging
      // Pause autoplay while dragging
      this.stopAutoplay();
    },
    drag(event) {
      if (!this.isDragging) return;
      const currentX =
        event.type === "touchmove" ? event.touches[0].clientX : event.clientX;
      const diff = currentX - this.startX;
      this.translateX = this.initialTranslateX + diff;
    },
    stopDragging() {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.checkBoundaries();
      this.$refs.sliderTrack.style.transition = "transform 0.3s ease-out"; // Restore animation
      // Resume autoplay after dragging
      this.startAutoplay();
    },
    startAutoplay() {
      // Clear any existing interval
      this.stopAutoplay();
      // Set new interval to move one car forward every 5 seconds
      this.autoplayInterval = setInterval(() => {
        this.scrollRight();
      }, 2000);
    },
    stopAutoplay() {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
        this.autoplayInterval = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.car-gallery-section {
  position: relative;
  padding: 120px 0;
  overflow: hidden;
  color: #1a1a2e;
  background-color: #f8f9fc;
  width: 100%;
}

.header-wrapper {
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 2rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 3.5rem;
}

.section-title {
  font-size: 2.75rem;
  font-weight: 800;
  color: #09283f;
  letter-spacing: -0.5px;
  margin: 0;
  position: relative;
}

.section-title:after {
  content: "";
  position: absolute;
  left: 0;
  bottom: -12px;
  width: 80px;
  height: 4px;
  background: linear-gradient(135deg, #ee800a, #ff7043);
  border-radius: 2px;
}

.controls-wrapper {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.view-all {
  color: #09283f;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  margin-left: 1.25rem;
  padding: 0.5rem 1rem;
  border-radius: 30px;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.view-all:after {
  content: "→";
  margin-left: 8px;
  font-size: 1.1rem;
  transition: transform 0.3s ease;
  display: inline-block;
}

.view-all:hover {
  color: #ee800a;
  border-color: #ee800a;
}

.view-all:hover:after {
  transform: translateX(4px);
}

.slider-container {
  width: 100%;
  padding: 50px;
  overflow: hidden;
  position: relative;
  user-select: none; /* Prevent text selection during dragging */
  -webkit-user-select: none; /* For Safari */
}

.slider-track {
  display: flex;
  transition: transform 0.3s ease-out; /* Smooth transition on click */
  will-change: transform; /* Optimize animation performance */
}

.car-slide {
  width: 290px;
  margin-right: 20px; /* Gap between cards */
  flex-shrink: 0; /* Prevent shrinking */
  transition: transform 0.4s ease;
  cursor: pointer;
}

.car-slide:hover {
  transform: translateY(-12px);
}

.car-slide:hover .car-image {
  transform: scale(1.08);
}

.car-slide:hover .car-name {
  color: #ee800a;
}

.car-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.4s ease;
  position: relative;
  width: 290px;
}

.car-card:before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #ee800a, #ff7043);
}

.car-image-wrapper {
  width: 100%;
  height: 200px;
  overflow: hidden;
  position: relative;
}

.car-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s ease;
}

.car-name {
  padding: 1.25rem 1.5rem 0.5rem;
  font-size: 1.2rem;
  font-weight: 700;
  color: #09283f;
  margin: 0;
  transition: color 0.3s ease;
}

.car-price {
  padding: 0 1.5rem 1.5rem;
  margin: 0;
  font-size: 1rem;
  color: #555;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.car-price:before {
  content: "";
  display: inline-block;
  width: 6px;
  height: 6px;
  background-color: #ee800a;
  border-radius: 50%;
  margin-right: 8px;
}

.original-price {
  text-decoration: line-through;
  color: #999;
  font-size: 0.9rem;
  margin-left: 0.5rem;
}

.slider-arrow {
  background-color: white;
  border: none;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  cursor: pointer;
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  color: #09283f;
}

.slider-arrow:hover {
  background: linear-gradient(135deg, #ee800a, #ff7043);
  color: white;
  transform: scale(1.1);
}

@media (max-width: 768px) {
  .car-gallery-section {
    padding: 80px 0;
  }
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 2.5rem;
  }
  .section-title:after {
    display: none;
  }
  .controls-wrapper {
    margin-top: 30px;
    align-self: flex-start;
  }
  .slider-container {
    padding: 20px; /* Smaller padding on mobile */
  }
}
</style>
